/**
 * AI（Gemini）の利用箇所の設定ページ（PC 向け）
 *
 * 左: 画面ごとのグループ / 中央: 選んだ画面の機能の表 / 右: 選んだ機能の設定とプロンプト。
 * 初期値はバックエンドの api/config/ai_usages.py。プロンプト以外はここから変えられる（埋め込みは変えない）。
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { getAiModels, getAiUsages } from '@/api/services/ai-usages'
import type { AiModelsResponse, AiUsageItem, AiUsagesResponse } from '@/api/types/ai-usages'
import { Header } from '@/components/shared/header'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useGoBack } from '@/hooks/useGoBack'

import { AiUsageDetail } from './components/ai-usage-detail'
import { AiUsageSidebar, ALL_GROUPS } from './components/ai-usage-sidebar'
import { AiUsageTable } from './components/ai-usage-table'
import { filterItems } from './components/ai-usage-utils'

const KeyBadge = ({ label, present }: { label: string; present: boolean }) => (
  <Badge variant={present ? 'secondary' : 'outline'} className={present ? undefined : 'text-destructive'}>
    {label}: {present ? 'あり' : 'なし'}
  </Badge>
)

export const SettingsAiUsagesPage = () => {
  const goBack = useGoBack()
  const [usages, setUsages] = useState<AiUsagesResponse | null>(null)
  const [catalog, setCatalog] = useState<AiModelsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [group, setGroup] = useState(ALL_GROUPS)
  const [query, setQuery] = useState('')
  const [changedOnly, setChangedOnly] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setUsages(await getAiUsages())
      setError(null)
    } catch {
      setError('AI の利用箇所を取得できませんでした')
    }
  }, [])

  useEffect(() => {
    void load()
    // モデル一覧は取得に数秒かかるので、表の表示を待たせずに別に引く
    getAiModels()
      .then(setCatalog)
      .catch(() => undefined)
  }, [load])

  const replaceItem = (saved: AiUsageItem) => {
    setUsages((current) =>
      current ? { ...current, items: current.items.map((item) => (item.key === saved.key ? saved : item)) } : current,
    )
  }

  const filtered = useMemo(
    () => (usages ? filterItems(usages.items, { query, changedOnly }) : []),
    [usages, query, changedOnly],
  )
  const visible = group === ALL_GROUPS ? filtered : filtered.filter((item) => item.group === group)
  const selected = usages?.items.find((item) => item.key === selectedKey) ?? null
  const changedCount = usages?.items.filter((item) => item.customized).length ?? 0

  return (
    <div className="flex h-dvh flex-col bg-background">
      <Header title="AI の利用箇所" onClose={goBack} />

      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2">
        <Input
          type="search"
          value={query}
          placeholder="機能名・モデル名・ファイル名で検索"
          className="h-8 w-80"
          onChange={(e) => setQuery(e.target.value)}
        />
        <label htmlFor="ai-usages-changed-only" className="flex items-center gap-2 text-sm">
          <Switch id="ai-usages-changed-only" checked={changedOnly} onCheckedChange={setChangedOnly} />
          変更済みのみ（{changedCount}）
        </label>
        {usages && (
          <div className="ml-auto flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <KeyBadge label="無料のキー" present={usages.has_free_key} />
            <KeyBadge label="有料のキー" present={usages.has_paid_key} />
            <Badge
              variant="outline"
              className={
                usages.paid_today_total > 0 ? 'border-amber-500/50 text-amber-600 dark:text-amber-400' : undefined
              }
              title="今日、有料のキーで答えが返った回数の合計"
            >
              今日の有料: {usages.paid_today_total} 回
            </Badge>
            <span>無料枠は日本時間の 16〜17 時にリセット</span>
          </div>
        )}
      </div>

      {error && <p className="p-4 text-sm text-destructive">{error}</p>}

      {usages && (
        <div className="grid min-h-0 flex-1 grid-cols-[11rem_minmax(0,1fr)_26rem] xl:grid-cols-[13rem_minmax(0,1fr)_30rem]">
          <aside className="overflow-y-auto border-r p-3">
            <AiUsageSidebar groups={usages.groups} items={filtered} selected={group} onSelect={setGroup} />
          </aside>

          <main className="overflow-y-auto">
            <AiUsageTable
              items={visible}
              groups={usages.groups}
              showGroupHeadings={group === ALL_GROUPS}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
            />
          </main>

          <aside className="overflow-y-auto border-l p-4">
            {selected ? (
              <AiUsageDetail item={selected} catalog={catalog} onSaved={replaceItem} />
            ) : (
              <p className="py-16 text-center text-sm text-muted-foreground">
                中央の表から機能を選ぶと、設定とプロンプトを表示します
              </p>
            )}
          </aside>
        </div>
      )}
    </div>
  )
}
